import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import propertyService from '../../services/propertyService';
import FormField from '../common/FormField';
import Alert from '../common/Alert';
import LoadingSpinner from '../common/LoadingSpinner';
import { SA_PROVINCES, PROPERTY_TYPES } from '../../utils/propertyOptions';
import '../../pages/ApplicantPages.css';
import './PropertyPages.css';

const EMPTY = {
  address_line1: '', address_line2: '', city: '', province: '', postal_code: '',
  monthly_rent: '', deposit: '', occupation_date: '', property_type: 'apartment'
};

const PROVINCE_OPTIONS = SA_PROVINCES.map((p) => ({ value: p, label: p }));

// Add a property (organisation required) or edit one (id in the URL).
const PropertyForm = ({ organisation }) => {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!editing) return;
    propertyService
      .get(id)
      .then((p) =>
        setValues({
          address_line1: p.address_line1 || '', address_line2: p.address_line2 || '', city: p.city || '',
          province: p.province || '', postal_code: p.postal_code || '',
          monthly_rent: p.monthly_rent_amount ?? '', deposit: p.deposit_amount ?? '',
          occupation_date: p.occupation_date || '', property_type: p.property_type || 'apartment'
        })
      )
      .catch((err) => setError(err.response?.data?.message || 'This property could not be loaded.'))
      .finally(() => setLoading(false));
  }, [editing, id]);

  const change = (e) => setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!values.address_line1.trim()) errs.address_line1 = 'Enter the street address.';
    if (!values.city.trim()) errs.city = 'Enter the city or town.';
    if (!values.province) errs.province = 'Choose a province.';
    if (!/^\d{4}$/.test(values.postal_code)) errs.postal_code = 'The postal code is 4 digits.';
    if (values.monthly_rent === '' || Number(values.monthly_rent) < 0) errs.monthly_rent = 'Enter the monthly rent.';
    if (values.deposit === '' || Number(values.deposit) < 0) errs.deposit = 'Enter the deposit (0 if none).';
    if (!values.occupation_date) errs.occupation_date = 'Choose the date it is available from.';
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    setError('');
    const payload = { ...values, monthly_rent: Number(values.monthly_rent), deposit: Number(values.deposit), address_line2: values.address_line2 || null };
    try {
      if (editing) {
        await propertyService.update(id, payload);
        navigate(`/properties/${id}`);
      } else {
        const property = await propertyService.create(organisation.id, payload);
        // The point of adding a property is to get applicants, so the link is switched on straight away.
        // It stays private until it is shared, and can be turned off from the property page.
        await propertyService.enableApplyLink(property.id).catch(() => {});
        navigate(`/properties/${property.id}?created=1`);
      }
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      if (apiErrors) setErrors(Object.fromEntries(Object.entries(apiErrors).map(([k, v]) => [k, v[0]])));
      else setError(err.response?.data?.message || 'The property could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="ap-page">
      <p><Link to={editing ? `/properties/${id}` : '/properties'}>&larr; {editing ? 'Back to property' : 'Properties'}</Link></p>
      <h1>{editing ? 'Edit property' : 'Add a property'}</h1>
      {error && <Alert type="error" message={error} />}

      <form className="ap-card" onSubmit={submit} noValidate>
        <FormField label="Street address" name="address_line1" value={values.address_line1} onChange={change} error={errors.address_line1} required autoComplete="off" />
        <FormField label="Unit, building or complex (optional)" name="address_line2" value={values.address_line2} onChange={change} error={errors.address_line2} />
        <div className="ap-row">
          <FormField label="City or town" name="city" value={values.city} onChange={change} error={errors.city} required />
          <FormField label="Province" name="province" type="select" options={PROVINCE_OPTIONS} value={values.province} onChange={change} error={errors.province} required />
        </div>
        <div className="ap-row">
          <FormField label="Postal code" name="postal_code" value={values.postal_code} onChange={change} error={errors.postal_code} required inputMode="numeric" maxLength={4} />
          <FormField label="Type" name="property_type" type="select" options={PROPERTY_TYPES} value={values.property_type} onChange={change} error={errors.property_type} required />
        </div>
        <div className="ap-row">
          <FormField label="Monthly rent (R)" name="monthly_rent" type="number" min="0" step="any" value={values.monthly_rent} onChange={change} error={errors.monthly_rent} required inputMode="decimal" />
          <FormField label="Deposit (R)" name="deposit" type="number" min="0" step="any" value={values.deposit} onChange={change} error={errors.deposit} required inputMode="decimal" />
        </div>
        <FormField label="Available from" name="occupation_date" type="date" value={values.occupation_date} onChange={change} error={errors.occupation_date} required />

        <div className="prop-actions">
          <button type="submit" className="btn-primary ap-button" disabled={saving}>
            {saving ? 'Saving...' : editing ? 'Save changes' : 'Add property and get link'}
          </button>
          <Link to={editing ? `/properties/${id}` : '/properties'} className="ap-button ap-button--ghost">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

export default PropertyForm;
